// This phone's own identity on a location's mesh: its source ADDRESS and its
// SEQUENCE NUMBER. Kept in localStorage, one entry per location.
//
// ⚠️ THIS IS THE ONE THING THAT MUST BE RIGHT FROM DAY ONE.
// A mesh node remembers, per source address, the highest sequence number it
// has accepted, and SILENTLY DROPS anything at or below it (replay
// protection). So:
//
//   • Every phone needs its OWN source address. Two family phones sharing one
//     would each see half their commands vanish — whichever phone is "behind"
//     on the counter is ignored, with no error anywhere. The server hands out a
//     fresh address per phone (GetMeshNetwork → ClientAddress, from
//     Location.MeshNextClient), and this file remembers it.
//
//   • The sequence number must never go backwards for that address. It is
//     persisted AHEAD of use (SEQ_RESERVE): on load we jump past everything that
//     could have been handed out before the app was killed, so a crash between
//     "used 1234" and "saved 1234" can only skip numbers, never repeat them.
//
//   • If this storage is wiped (reinstall, cleared data) the phone simply asks
//     for a NEW address. A fresh address starts at seq 0 safely; reusing the old
//     address at seq 0 would be ignored by every lamp. Never "restore" an
//     address without its counter.
//
// The entry is also tied to the network: a different NetKey (network wiped
// and recreated) discards it.

const PREFIX = 'sli.mesh.client.';
const SEQ_RESERVE = 256;
const SEQ_MAX = 0xffffff;

const read = (locationId) => {
  try {
    const raw = localStorage.getItem(PREFIX + locationId);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const write = (locationId, entry) => {
  try {
    localStorage.setItem(PREFIX + locationId, JSON.stringify(entry));
  } catch (e) {
    // Storage refused: the counter still advances in memory for this session.
  }
};

/** The stored address for this location+network, or null if none / stale. */
export function storedClientAddress(locationId, networkId) {
  const e = read(locationId);
  if (!e || e.networkId !== networkId || !e.address) return null;
  return Number(e.address);
}

/**
 * The address this phone last used at a location, whatever the network — what
 * it offers GetMeshNetwork before it knows the NetKey. The server hands it back
 * only if it issued it; openClient then restores the counter only if the
 * network matches too.
 */
export function lastClientAddress(locationId) {
  const e = read(locationId);
  return e && e.address ? Number(e.address) : null;
}

/**
 * -> { address, nextSeq() } for this location. `address` is what the server
 * confirmed; the counter is restored (and jumped ahead) from storage when the
 * entry matches this address and network, else starts at 0.
 */
export function openClient(locationId, networkId, address) {
  const prev = read(locationId);
  const same = prev && prev.networkId === networkId && Number(prev.address) === Number(address);
  const state = {
    seq: same ? Number(prev.savedUpTo) || 0 : 0,
    savedUpTo: 0,
  };
  const persist = () => {
    state.savedUpTo = state.seq + SEQ_RESERVE;
    write(locationId, { networkId, address, savedUpTo: state.savedUpTo });
  };
  persist();
  return {
    address: Number(address),
    nextSeq: () => {
      const s = state.seq;
      state.seq += 1;
      if (state.seq >= state.savedUpTo) persist();
      if (s > SEQ_MAX) throw new Error('This phone has used up its mesh sequence numbers. Clear the app data to get a new address.');
      return s;
    },
    /** For the settings line only. */
    peekSeq: () => state.seq,
  };
}

/** Forget this phone's entry for a location (it will ask for a new address). */
export function forgetClient(locationId) {
  try { localStorage.removeItem(PREFIX + locationId); } catch (e) { /* ignore */ }
}
